import { useState, useEffect } from "react";
import {
    Box,
    Button,
    Checkbox,
    VStack,
    HStack,
    Text,
    Badge,
    Spinner,
    SimpleGrid,
    Divider,
    Flex,
    IconButton,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverHeader,
    PopoverBody,
    PopoverArrow,
    PopoverCloseButton,
    Table,
    Tbody,
    Tr,
    Td,
    createStandaloneToast,
} from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import Config from "config";

const toast = createStandaloneToast();
const HOST = Config.Host;

function PenaltyInfoPopover({ penalty }) {
    return (
        <Popover placement="right" trigger="hover">
            <PopoverTrigger>
                <IconButton
                    icon={<InfoIcon />}
                    size="xs"
                    variant="ghost"
                    aria-label="Penalty info"
                    ml={1}
                />
            </PopoverTrigger>
            <PopoverContent width="320px">
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverHeader fontWeight="bold">{penalty.label}</PopoverHeader>
                <PopoverBody>
                    {penalty.description && (
                        <Text fontSize="sm" color="gray.600" mb={3} fontStyle="italic">
                            {penalty.description}
                        </Text>
                    )}
                    <Table size="sm" variant="unstyled">
                        <Tbody>
                            <Tr>
                                <Td py={1} fontWeight="medium">ID</Td>
                                <Td py={1}><code>{penalty.id}</code></Td>
                            </Tr>
                            <Tr>
                                <Td py={1} fontWeight="medium">Message</Td>
                                <Td py={1}><code>{penalty.message}</code></Td>
                            </Tr>
                            <Tr>
                                <Td py={1} fontWeight="medium">Delay</Td>
                                <Td py={1}>{penalty.minDuration}ms</Td>
                            </Tr>
                            <Tr>
                                <Td py={1} fontWeight="medium">Difficulty</Td>
                                <Td py={1}>{penalty.difficulty}</Td>
                            </Tr>
                            <Tr>
                                <Td py={1} fontWeight="medium">Zeros</Td>
                                <Td py={1}>{penalty.zeros}</Td>
                            </Tr>
                            <Tr>
                                <Td py={1} fontWeight="medium">V2 Tokens</Td>
                                <Td py={1}>{penalty.v2Tokens}</Td>
                            </Tr>
                            {penalty.pass2 && (
                                <Tr>
                                    <Td py={1} fontWeight="medium">Pass2 Threshold</Td>
                                    <Td py={1}>{penalty.pass2Threshold}</Td>
                                </Tr>
                            )}
                        </Tbody>
                    </Table>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
}

export default function ForcedPenalties({ customerID, forcePenalties = [] }) {
    const [penalties, setPenalties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(new Set(forcePenalties || []));
    const [saving, setSaving] = useState(false);

    // Fetch penalties from API
    useEffect(() => {
        fetch(`${HOST}/cc/penalties?cors=true`, {
            credentials: "include",
        })
            .then((r) => r.json())
            .then((data) => {
                setPenalties(data);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Failed to fetch penalties:", err);
                setLoading(false);
                toast({
                    title: "Error",
                    description: "Failed to load penalties list",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            });
    }, []);

    // Update selected when forcePenalties prop changes
    useEffect(() => {
        setSelected(new Set(forcePenalties || []));
    }, [forcePenalties]);

    const handleToggle = (penaltyId) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(penaltyId)) {
                next.delete(penaltyId);
            } else {
                next.add(penaltyId);
            }
            return next;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await fetch(
                `${HOST}/cc/customers/${customerID}/key/forcePenalties?cors=true`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ value: Array.from(selected) }),
                }
            );
            if (response.ok) {
                toast({
                    title: "Saved",
                    description: "Forced penalties updated successfully",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                throw new Error("Failed to save");
            }
        } catch (err) {
            toast({
                title: "Error",
                description: "Failed to update forced penalties",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setSaving(false);
        }
    };

    const formatDelay = (ms) => {
        if (ms >= 1000) {
            return `${ms / 1000}s`;
        }
        return `${ms}ms`;
    };

    const selectAllExperimental = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            penalties.filter((p) => p.experimental).forEach((p) => next.add(p.id));
            return next;
        });
    };

    const deselectAllExperimental = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            penalties.filter((p) => p.experimental).forEach((p) => next.delete(p.id));
            return next;
        });
    };

    if (loading) {
        return (
            <Flex justify="center" align="center" h="200px">
                <Spinner size="xl" />
            </Flex>
        );
    }

    const experimentalPenalties = penalties.filter((p) => p.experimental);
    const standardPenalties = penalties.filter((p) => !p.experimental);

    return (
        <Box>
            <VStack align="stretch" spacing={4}>
                <Box>
                    <Text fontWeight="bold" fontSize="lg" mb={2}>
                        Experimental Penalties
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={3}>
                        These penalties require explicit opt-in via forcePenalties
                    </Text>
                    <HStack mb={3}>
                        <Button size="sm" onClick={selectAllExperimental}>
                            Select All
                        </Button>
                        <Button size="sm" onClick={deselectAllExperimental}>
                            Deselect All
                        </Button>
                    </HStack>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                        {experimentalPenalties.map((penalty) => (
                            <HStack
                                key={penalty.id}
                                p={2}
                                borderWidth="1px"
                                borderRadius="md"
                                bg={selected.has(penalty.id) ? "green.50" : "transparent"}
                                justify="space-between"
                            >
                                <HStack>
                                    <Checkbox
                                        isChecked={selected.has(penalty.id)}
                                        onChange={() => handleToggle(penalty.id)}
                                    >
                                        <Text fontSize="sm">{penalty.label}</Text>
                                    </Checkbox>
                                    <PenaltyInfoPopover penalty={penalty} />
                                </HStack>
                                <Badge colorScheme="orange" fontSize="xs">
                                    {formatDelay(penalty.minDuration)}
                                </Badge>
                            </HStack>
                        ))}
                    </SimpleGrid>
                </Box>

                <Divider />

                <Box>
                    <Text fontWeight="bold" fontSize="lg" mb={2}>
                        Standard Penalties
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={3}>
                        These are always active (shown for reference)
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                        {standardPenalties.map((penalty) => (
                            <HStack
                                key={penalty.id}
                                p={2}
                                borderWidth="1px"
                                borderRadius="md"
                                opacity={0.7}
                                justify="space-between"
                            >
                                <HStack>
                                    <Checkbox isChecked isDisabled>
                                        <Text fontSize="sm">{penalty.label}</Text>
                                    </Checkbox>
                                    <PenaltyInfoPopover penalty={penalty} />
                                </HStack>
                                <Badge colorScheme="blue" fontSize="xs">
                                    {formatDelay(penalty.minDuration)}
                                </Badge>
                            </HStack>
                        ))}
                    </SimpleGrid>
                </Box>

                <Button
                    colorScheme="teal"
                    onClick={handleSave}
                    isLoading={saving}
                    loadingText="Saving..."
                    mt={4}
                >
                    Save Forced Penalties
                </Button>
            </VStack>
        </Box>
    );
}
